import { Injectable, inject } from '@angular/core';
import { Folder } from '@core/types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PlatformSignalService } from '@unified-ui/shared/services';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { UserFoldersApiService } from '../services/user-folders-api.service';
import { FoldersAPICall, FoldersAPILoadingState, UserFoldersApiSignalService } from '../services/user-folders-signal.service';
import { createUserFolderSuccess, deleteUserFolder, getAllUserFolders, getAllUserFoldersSuccess, saveUserFolder, setReportFolder, setScheduleFolder, updateUserFolderSuccess } from './user-folders.actions';

@Injectable()
export class UserFoldersEffects {
    private actions$ = inject(Actions);
    private _userFoldersApiService = inject(UserFoldersApiService);
    private _loadingService = inject(UserFoldersApiSignalService);
    private _platformSignalService = inject(PlatformSignalService);

    /************************************************************************
     *  FIND/GET USER FOLDERS
     ************************************************************************/
    public getAllUserFolders$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getAllUserFolders),
            tap(() => this._loadingService.setLoadingState(FoldersAPICall.GET_ALL_USER_FOLDERS, FoldersAPILoadingState.LOADING)),
            switchMap(() => {
                return this._userFoldersApiService.getAllUserFolders().pipe(
                    map(data => {
                        this._loadingService.setLoadingState(FoldersAPICall.GET_ALL_USER_FOLDERS, FoldersAPILoadingState.LOADED);
                        return getAllUserFoldersSuccess({ folders: data.data.findAllUserFolders ?? [] });
                    }),
                    catchError(() => {
                        this._loadingService.setLoadingState(FoldersAPICall.GET_ALL_USER_FOLDERS, FoldersAPILoadingState.ERROR);
                        this._platformSignalService.platformSignal = {
                            severity: 'error',
                            summary: 'Error Saving Folder',
                            detail: 'Unable to load folders. Please contact the administrator.'
                        };
                        return of();
                    })
                );
            })
        )
    );

    /************************************************************************
     *  CREATE/UPDATE USER FOLDERS
     ************************************************************************/

    public saveUserFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveUserFolder),
            tap(() => this._loadingService.setLoadingState(FoldersAPICall.SAVE_FOLDER, FoldersAPILoadingState.LOADING)),
            switchMap(input => {
                return this._userFoldersApiService.saveFolder(input.folder).pipe(
                    map(data => {
                        const newOrUpdatedFolder = { ...data.data?.saveFolder, parent: input.folder.parentFolderId, description: input.folder.description } as Folder;
                        this._loadingService.setLoadingState(FoldersAPICall.SAVE_FOLDER, FoldersAPILoadingState.LOADED);

                        // Show success notification
                        const isUpdate = !!input.folder.id;
                        this._platformSignalService.platformSignal = {
                            severity: 'success',
                            summary: isUpdate ? 'Folder Updated' : 'Folder Created',
                            detail: isUpdate ? `The folder "${data.data?.saveFolder.name}" has been updated successfully.` : `The folder "${data.data?.saveFolder.name}" has been created successfully.`
                        };

                        // update a folder
                        if (isUpdate) {
                            return updateUserFolderSuccess({
                                folder: {
                                    id: input.folder.id!,
                                    changes: {
                                        parent: input.folder.parentFolderId,
                                        name: data.data?.saveFolder.name,
                                        description: input.folder.description
                                    }
                                }
                            });
                            // create a new folder
                        } else {
                            return createUserFolderSuccess({ folder: newOrUpdatedFolder });
                        }
                    }),
                    catchError(err => {
                        this._loadingService.setLoadingState(FoldersAPICall.SAVE_FOLDER, FoldersAPILoadingState.ERROR);

                        // Show error notification
                        this._platformSignalService.platformSignal = {
                            severity: 'error',
                            summary: 'Error',
                            detail: `Failed to ${input.folder.id ? 'update' : 'create'} folder. Please try again.`
                        };

                        return of();
                    })
                );
            })
        )
    );

    /************************************************************************
     *  DELETE USER FOLDER
     ************************************************************************/

    public deleteUserFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteUserFolder),
            tap(() => this._loadingService.setLoadingState(FoldersAPICall.DELETE_FOLDER, FoldersAPILoadingState.LOADING)),
            switchMap(action => {
                return this._userFoldersApiService.deleteFolder(Number(action.id)).pipe(
                    map(() => {
                        // update folders list
                        this._loadingService.setLoadingState(FoldersAPICall.DELETE_FOLDER, FoldersAPILoadingState.LOADED);
                        // Show success notification
                        this._platformSignalService.platformSignal = {
                            severity: 'success',
                            summary: 'Folder Deleted',
                            detail: 'The folder has been deleted successfully.'
                        };
                        return getAllUserFolders();
                    }),
                    catchError(() => {
                        this._loadingService.setLoadingState(FoldersAPICall.DELETE_FOLDER, FoldersAPILoadingState.ERROR);
                        this._platformSignalService.platformSignal = {
                            severity: 'error',
                            summary: 'Error Saving Folder',
                            detail: 'Unable to delete folder. Please try again.'
                        };
                        return of();
                    })
                );
            })
        )
    );

    /************************************************************************
     *  SET REPORT FOLDER
     ************************************************************************/

    public setReportFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setReportFolder),
            tap(() => this._loadingService.setLoadingState(FoldersAPICall.SET_REPORT_FOLDER, FoldersAPILoadingState.LOADING)),
            switchMap(action => {
                return this._userFoldersApiService.setReportFolder(action.reportFolderInput).pipe(
                    map(() => {
                        // update folders list
                        this._loadingService.setLoadingState(FoldersAPICall.SET_REPORT_FOLDER, FoldersAPILoadingState.LOADED);
                        return getAllUserFolders();
                    }),
                    catchError(() => {
                        this._loadingService.setLoadingState(FoldersAPICall.SET_REPORT_FOLDER, FoldersAPILoadingState.ERROR);
                        this._platformSignalService.platformSignal = {
                            severity: 'error',
                            summary: 'Error Saving Folder',
                            detail: 'Unable to move the report. Please contact the administrator.'
                        };
                        return of();
                    })
                );
            })
        )
    );

    /************************************************************************
     *  SET SCHEDULE FOLDER
     ************************************************************************/

    public setScheduleFolder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setScheduleFolder),
            tap(() => this._loadingService.setLoadingState(FoldersAPICall.SET_SCHEDULE_FOLDER, FoldersAPILoadingState.LOADING)),
            switchMap(action => {
                return this._userFoldersApiService.setScheduleFolder(action.scheduleFolderInput).pipe(
                    map(() => {
                        // update folders list
                        this._loadingService.setLoadingState(FoldersAPICall.SET_SCHEDULE_FOLDER, FoldersAPILoadingState.LOADED);
                        return getAllUserFolders();
                    }),
                    catchError(() => {
                        this._loadingService.setLoadingState(FoldersAPICall.SET_SCHEDULE_FOLDER, FoldersAPILoadingState.ERROR);
                        this._platformSignalService.platformSignal = {
                            severity: 'error',
                            summary: 'Error Saving Folder',
                            detail: 'Unable to move the schedule. Please contact the administrator.'
                        };
                        return of();
                    })
                );
            })
        )
    );
}
